import { Alert, Form, message, Modal, Radio, Select, Switch } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SystemDescriptions } from "src/common/descriptions";
import { Quotation, QuotationDocumentType, QuotationTemplate, QuotationTemplateType } from "src/common/models/quotation";
import { getInputRequiredRule } from "src/common/util";
import { serviceLoading } from "src/common/apiLoader/state/selection";
import { RootState } from "src/state/reducer";
import { cleanQuotationFile, generateQuotationPdfApi, getQuotationTemplatesApi, sendQuotationApi } from "../../state/action";
import { GENERATE_QUOTATION_PDF_API, GET_QUOTATION_TEMPLATES_API, SEND_QUOTATION_API } from "../../state/actionTypes";

export enum QuotationPrintAction {
    PDF = "PDF",
    TELEGRAM = "TELEGRAM",
}

interface Props {
    open: boolean
    action: QuotationPrintAction
    quotationId?: number
    quotationCode?: string
    quotationName?: string
    onCancel: () => void
}

export type ReduxProps = {
    currentQuotation?: Partial<Quotation>
    templates?: QuotationTemplate[]
    quotationFile?: any
    isLoadingTemplates: boolean
    isGeneratingPdf: boolean
    isSendingQuotation: boolean
    sendQuotationSuccess: boolean
}

export const QuotationPrintModal = (props: Props) => {
    const dispatch = useDispatch()
    const [form] = Form.useForm()
    const [selectedTemplate, setSelectedTemplate] = useState<QuotationTemplate | undefined>(undefined)
    const [selectedAction, setSelectedAction] = useState<QuotationPrintAction>(props.action)
    const [downloadClicked, setDownloadClicked] = useState<boolean>(false)
    const descriptions = SystemDescriptions.PAGES.QUOTATION.CREATE.PDF_ACTIONS

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        currentQuotation: state.quotation.currentQuotation,
        templates: state.quotation.quotationTemplates,
        quotationFile: state.quotation.quotationFile,
        isLoadingTemplates: serviceLoading(state, [GET_QUOTATION_TEMPLATES_API]),
        isGeneratingPdf: serviceLoading(state, [GENERATE_QUOTATION_PDF_API]),
        isSendingQuotation: serviceLoading(state, [SEND_QUOTATION_API]),
        sendQuotationSuccess: state.quotation.sendQuotationSuccess,
    }))

    useEffect(() => {
        if (props.open && (!reduxProps.templates || reduxProps.templates.length === 0)) {
            dispatch(getQuotationTemplatesApi())
        }
    }, [dispatch, props.open, reduxProps.templates])

    useEffect(() => {
        if (props.open) {
            setSelectedAction(props.action)
        }
    }, [props.open, props.action])

    const activeTemplates = useMemo(
        () => {
            const expectedType = reduxProps.currentQuotation?.documentType === QuotationDocumentType.PROPOSAL
                ? QuotationTemplateType.PROPOSAL
                : QuotationTemplateType.QUOTATION

            return reduxProps.templates?.filter(template => template.isActive && template.type === expectedType) || []
        },
        [reduxProps.currentQuotation?.documentType, reduxProps.templates],
    )
    const hasLoadedTemplates = Boolean(reduxProps.templates)
    const hasActiveTemplates = activeTemplates.length > 0

    useEffect(() => {
        if (!props.open || activeTemplates.length !== 1) {
            return
        }

        const [template] = activeTemplates
        if (!template.id || form.getFieldValue("templateId")) {
            return
        }

        form.setFieldsValue({ templateId: template.id.toString() })
        setSelectedTemplate(template)
    }, [props.open, activeTemplates, form])

    useEffect(() => {
        if (!reduxProps.quotationFile || !downloadClicked) {
            return
        }

        const url: string = window.URL.createObjectURL(reduxProps.quotationFile as any)
        const a: HTMLAnchorElement = document.createElement("a")
        a.href = url
        a.download = `${props.quotationCode || reduxProps.currentQuotation?.code || props.quotationName || reduxProps.currentQuotation?.name || "quotation"}.pdf`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
        setDownloadClicked(false)
        dispatch(cleanQuotationFile())
        props.onCancel()
    }, [dispatch, reduxProps.quotationFile, downloadClicked, reduxProps.currentQuotation?.code, reduxProps.currentQuotation?.name, props])

    useEffect(() => {
        if (reduxProps.sendQuotationSuccess && props.open && selectedAction === QuotationPrintAction.TELEGRAM) {
            message.success(descriptions.SENT_SUCCESS)
            props.onCancel()
        }
    }, [reduxProps.sendQuotationSuccess, props, selectedAction, descriptions.SENT_SUCCESS])

    const handleTemplateChange = (templateId: string) => {
        const template = activeTemplates.find(item => item.id?.toString() === templateId)
        setSelectedTemplate(template)
    }

    const buildRequest = () => {
        const values = form.getFieldsValue()
        return {
            templateId: Number(values.templateId),
            showPrices: Boolean(values.showPrices),
        }
    }

    const handleSubmit = async () => {
        const quotationId = props.quotationId || reduxProps.currentQuotation?.id
        if (!quotationId) {
            message.error(descriptions.SAVE_FIRST)
            return
        }

        try {
            await form.validateFields()
        } catch {
            return
        }

        if (selectedAction === QuotationPrintAction.PDF) {
            dispatch(generateQuotationPdfApi(quotationId, buildRequest()))
            setDownloadClicked(true)
            return
        }

        dispatch(sendQuotationApi(quotationId, buildRequest()))
    }

    const handleCancel = () => {
        form.resetFields()
        setSelectedTemplate(undefined)
        setSelectedAction(props.action)
        setDownloadClicked(false)
        dispatch(cleanQuotationFile())
        props.onCancel()
    }

    return (
        <Modal
            centered
            open={props.open}
            title={descriptions.TITLE}
            okText={selectedAction === QuotationPrintAction.PDF ? descriptions.GENERATE_PDF : descriptions.SEND_TELEGRAM}
            cancelText={descriptions.BACK}
            onOk={handleSubmit}
            onCancel={handleCancel}
            confirmLoading={reduxProps.isGeneratingPdf || reduxProps.isSendingQuotation}
            okButtonProps={{ disabled: hasLoadedTemplates && !hasActiveTemplates }}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                autoComplete="none"
            >
                <Form.Item label={descriptions.ACTION}>
                    <Radio.Group
                        optionType="button"
                        buttonStyle="solid"
                        value={selectedAction}
                        onChange={(event) => setSelectedAction(event.target.value)}
                    >
                        <Radio.Button value={QuotationPrintAction.PDF}>
                            {descriptions.GENERATE_PDF}
                        </Radio.Button>
                        <Radio.Button value={QuotationPrintAction.TELEGRAM}>
                            {descriptions.SEND_TELEGRAM}
                        </Radio.Button>
                    </Radio.Group>
                </Form.Item>
                <Form.Item
                    label={descriptions.TEMPLATE}
                    name="templateId"
                    rules={[getInputRequiredRule(descriptions.TEMPLATE)]}
                >
                    <Select
                        placeholder={descriptions.SELECT_TEMPLATE}
                        options={activeTemplates.map(template => ({
                            value: template.id?.toString() || "",
                            label: template.name,
                        }))}
                        loading={reduxProps.isLoadingTemplates}
                        onChange={handleTemplateChange}
                    />
                </Form.Item>
                {
                    hasLoadedTemplates && !hasActiveTemplates &&
                    <Alert
                        type="warning"
                        showIcon
                        message={descriptions.NO_ACTIVE_TEMPLATES}
                        description={descriptions.ACTIVATE_TEMPLATE_HINT}
                    />
                }
                {
                    selectedTemplate?.type === QuotationTemplateType.PROPOSAL &&
                    <Form.Item
                        label={descriptions.SHOW_PRICES}
                        name="showPrices"
                        valuePropName="checked"
                        initialValue={true}
                    >
                        <Switch />
                    </Form.Item>
                }
            </Form>
        </Modal>
    )
}
